import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Badge,
  Text,
  VStack,
  HStack,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useColorModeValue,
  Alert,
  AlertIcon,
  Code,
  SimpleGrid,
  VisuallyHidden,
  Spinner,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  useApproveMerchantChain,
  useMerchant,
  useMerchantOrderDefaults,
  useMerchantChains,
  useRecomputeMerchantSnapshot,
  useRefreshMerchantOrderDefaults,
  useRejectMerchantChain,
  useUpdateMerchantStatus,
} from '@/hooks/useAdminData'
import { useEffect, useState } from 'react'
import { StatusConfirmDialog } from './StatusConfirmDialog'
import { formatDateTime } from '@/utils/formatUtils'
import { SectionSkeleton } from '@/components/common/LoadingSkeleton'
import type { MerchantChainAccess, MerchantChainCompatibility } from '@/types'
import { MerchantChainReviewDialog } from './MerchantChainReviewDialog'
import { useToast } from '@/hooks/useToast'
import { FiRefreshCw } from 'react-icons/fi'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface MerchantDetailModalProps {
  merchantId: string | null
  isOpen: boolean
  onClose: () => void
  showUserId?: boolean
}

export function MerchantDetailModal({ merchantId, isOpen, onClose, showUserId = true }: MerchantDetailModalProps) {
  const { t } = useTranslation()
  const formatOrderDefaultsDuration = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return t('merchantDetail.orderDefaults.durationSecondsShort', {
        count: 0,
      })
    }
    if (seconds % 86400 === 0) {
      return t('merchantDetail.orderDefaults.durationDaysShort', {
        count: seconds / 86400,
      })
    }
    if (seconds % 3600 === 0) {
      return t('merchantDetail.orderDefaults.durationHoursShort', {
        count: seconds / 3600,
      })
    }
    if (seconds % 60 === 0) {
      return t('merchantDetail.orderDefaults.durationMinutesShort', {
        count: seconds / 60,
      })
    }
    return t('merchantDetail.orderDefaults.durationSecondsShort', {
      count: seconds,
    })
  }
  const [confirmAction, setConfirmAction] = useState<{
    action: string
    merchantId: string
  } | null>(null)
  const [chainReviewAction, setChainReviewAction] = useState<{
    action: 'approve' | 'reject'
    compatibility: MerchantChainCompatibility
  } | null>(null)
  const [detailTabIndex, setDetailTabIndex] = useState(0)

  useEffect(() => {
    if (isOpen) {
      setDetailTabIndex(0)
    }
  }, [isOpen, merchantId])

  const {
    data: merchant,
    isLoading,
    isFetching: isMerchantFetching,
    error,
    refetch: refetchMerchant,
  } = useMerchant(merchantId || '')
  const {
    data: merchantChains,
    isLoading: isChainsLoading,
    isFetching: isChainsFetching,
    refetch: refetchMerchantChains,
  } = useMerchantChains(merchantId || '', { enabled: detailTabIndex === 1 })
  const {
    data: merchantOrderDefaults,
    isLoading: isOrderDefaultsLoading,
    isFetching: isOrderDefaultsFetching,
    refetch: refetchMerchantOrderDefaults,
  } = useMerchantOrderDefaults(merchantId || '', { enabled: detailTabIndex === 2 })
  const updateStatus = useUpdateMerchantStatus()
  const refreshOrderDefaults = useRefreshMerchantOrderDefaults()
  const recomputeSnapshot = useRecomputeMerchantSnapshot()
  const approveChain = useApproveMerchantChain()
  const rejectChain = useRejectMerchantChain()
  const toast = useToast()

  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200')
  const mutedCardBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.300')
  const labelTextColor = useColorModeValue('gray.600', 'gray.200')
  const successTextColor = useColorModeValue('green.600', 'green.300')
  const dangerTextColor = useColorModeValue('red.600', 'red.300')

  const normalizeStatusCode = (status: string | undefined | null) => {
    if (!status) return 'unknown'
    if (status.startsWith('mst_')) {
      return status
    }
    switch (status.toLowerCase()) {
      case 'pending_configuration':
      case 'pending_config':
        return 'mst_pending_config'
      case 'partial_active':
        return 'mst_partial_active'
      case 'active':
        return 'mst_active'
      case 'suspended':
        return 'mst_suspended'
      case 'terminated':
        return 'mst_terminated'
      default:
        return status
    }
  }

  const deriveAggregateStatus = (
    baseStatus: string | undefined | null,
    chains: MerchantChainAccess[] | undefined
  ) => {
    const normalizedBase = normalizeStatusCode(baseStatus)
    if (normalizedBase === 'mst_suspended' || normalizedBase === 'mst_terminated') {
      return normalizedBase
    }
    if (!chains || chains.length === 0) {
      return normalizedBase
    }
    const enabledChains = chains.filter((chain) => chain.requestedEnabled)
    if (enabledChains.length === 0) {
      return 'mst_pending_config'
    }
    const effectiveChains = enabledChains.filter((chain) => chain.effectiveEnabled)
    if (effectiveChains.length === 0) {
      return 'mst_pending_config'
    }
    if (effectiveChains.length < enabledChains.length) {
      return 'mst_partial_active'
    }
    return 'mst_active'
  }

  const normalizedStatus = deriveAggregateStatus(
    merchant?.status,
    merchantChains?.chains
  )
  const isRefreshing =
    isMerchantFetching ||
    (detailTabIndex === 1 && isChainsFetching) ||
    (detailTabIndex === 2 && isOrderDefaultsFetching)
  const isRefreshingOrderDefaults =
    isOrderDefaultsFetching || refreshOrderDefaults.isPending
  const isRecomputingSnapshot = recomputeSnapshot.isPending

  const getChainLabel = (compatibility: MerchantChainCompatibility) => {
    switch (compatibility) {
      case 'evm':
        return t('merchantDetail.chainLabels.evm')
      case 'tron':
        return t('merchantDetail.chainLabels.tron')
      case 'solana':
        return t('merchantDetail.chainLabels.solana')
      default:
        return compatibility
    }
  }

  const getApprovalBadgeColor = (status: MerchantChainAccess['approvalStatus']) => {
    switch (status) {
      case 'approved':
        return 'green'
      case 'pending_review':
        return 'blue'
      case 'rejected':
        return 'red'
      case 'not_submitted':
      default:
        return 'gray'
    }
  }

  const getApprovalLabel = (status: MerchantChainAccess['approvalStatus']) => {
    switch (status) {
      case 'approved':
        return t('merchantDetail.chainApproval.approved')
      case 'pending_review':
        return t('merchantDetail.chainApproval.pendingReview')
      case 'rejected':
        return t('merchantDetail.chainApproval.rejected')
      case 'not_submitted':
      default:
        return t('merchantDetail.chainApproval.notSubmitted')
    }
  }

  const getStatusColor = (status: string) => {
    const code = normalizeStatusCode(status)
    switch (code) {
      case 'mst_pending_config':
        return 'yellow'
      case 'mst_partial_active':
        return 'blue'
      case 'mst_active':
        return 'green'
      case 'mst_suspended':
        return 'red'
      case 'mst_terminated':
        return 'gray'
      default:
        return 'gray'
    }
  }

  const getStatusText = (status: string) => {
    const code = normalizeStatusCode(status)
    switch (code) {
      case 'mst_pending_config':
        return t('merchants.status.pendingConfig')
      case 'mst_partial_active':
        return t('merchants.status.partialActive')
      case 'mst_active':
        return t('merchants.status.active')
      case 'mst_suspended':
        return t('merchants.status.suspended')
      case 'mst_terminated':
        return t('merchants.status.terminated')
      default:
        return t('common.unknown')
    }
  }

  const getAdminStatusActions = (status: string) => {
    const code = normalizeStatusCode(status)
    switch (code) {
      case 'mst_pending_config':
      case 'mst_partial_active':
      case 'mst_active':
        return ['edit', 'suspend', 'terminate']
      case 'mst_suspended':
        return ['edit', 'activate', 'terminate']
      case 'mst_terminated':
        return []
      default:
        return []
    }
  }

  const handleActionClick = (action: string) => {
    if (!merchant) return


    if (action === 'edit') {
      return
    }

    setConfirmAction({
      action,
      merchantId: merchant.merchant_internal_id ?? merchant.id
    })
  }

  const getBooleanStatusPresentation = (value: boolean, trueLabel: string, falseLabel: string) => ({
    label: value ? trueLabel : falseLabel,
    color: value ? successTextColor : dangerTextColor,
  })

  const handleStatusUpdate = async (action: string, reason: string) => {
    if (!merchant) return

    let newStatus = ''
    switch (action) {
      case 'activate':
        newStatus = 'mst_active'
        break
      case 'suspend':
        newStatus = 'mst_suspended'
        break
      case 'terminate':
        newStatus = 'mst_terminated'
        break
      default:
        return
    }

    try {
      await updateStatus.mutateAsync({
        merchantId: merchant.merchant_internal_id ?? merchant.id,
        status: newStatus,
        reason
      })
      setConfirmAction(null)
      onClose() // Close detail modal
    } catch (error) {
      console.error('Failed to update merchant status:', error)
      const responseData = (error as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (error instanceof Error ? error.message : t('common.operationFailed'))

      toast.error({
        title: t('merchantDetail.statusUpdateFailedTitle', 'Status update failed'),
        description: errorMessage,
        duration: 6000,
      })
    }
  }

  const handleRefresh = async () => {
    const refreshes: Array<Promise<unknown>> = [refetchMerchant()]
    if (detailTabIndex === 1) {
      refreshes.push(refetchMerchantChains())
    }
    if (detailTabIndex === 2) {
      refreshes.push(refetchMerchantOrderDefaults())
    }
    await Promise.all(refreshes)
  }

  const handleRefreshOrderDefaults = async () => {
    if (!merchantId) return
    try {
      await refreshOrderDefaults.mutateAsync({ merchantId })
      await refetchMerchantOrderDefaults()
      toast.success({
        title: t('merchantDetail.orderDefaults.refreshSuccessTitle'),
        description: t('merchantDetail.orderDefaults.refreshSuccessDescription'),
        duration: 4000,
      })
    } catch (error) {
      const responseData = (error as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (error instanceof Error ? error.message : t('common.operationFailed'))

      toast.error({
        title: t('merchantDetail.orderDefaults.refreshFailedTitle', 'Refresh failed'),
        description: errorMessage,
        duration: 6000,
      })
    }
  }

  const handleRecomputeSnapshot = async () => {
    if (!merchantId) return
    try {
      await recomputeSnapshot.mutateAsync({ merchantId })
      await refetchMerchantOrderDefaults()
      toast.success({
        title: t('merchantDetail.orderDefaults.recomputeSuccessTitle'),
        description: t('merchantDetail.orderDefaults.recomputeSuccessDescription'),
        duration: 4000,
      })
    } catch (error) {
      const responseData = (error as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (error instanceof Error ? error.message : t('common.operationFailed'))

      toast.error({
        title: t('merchantDetail.orderDefaults.recomputeFailedTitle', 'Recompute failed'),
        description: errorMessage,
        duration: 6000,
      })
    }
  }

  const handleChainReviewConfirm = async (reason: string) => {
    if (!merchant || !chainReviewAction) return

    const merchantInternalId = merchant.merchant_internal_id ?? merchant.id
    try {
      if (chainReviewAction.action === 'approve') {
        await approveChain.mutateAsync({
          merchantId: merchantInternalId,
          compatibility: chainReviewAction.compatibility,
          reviewRevisionId: merchantChains?.chains.find(
            (chain) => chain.compatibility === chainReviewAction.compatibility
          )?.reviewRevisionId ?? '',
        })
      } else {
        await rejectChain.mutateAsync({
          merchantId: merchantInternalId,
          compatibility: chainReviewAction.compatibility,
          reason,
          reviewRevisionId: merchantChains?.chains.find(
            (chain) => chain.compatibility === chainReviewAction.compatibility
          )?.reviewRevisionId ?? '',
        })
      }
      setChainReviewAction(null)
    } catch (error) {
      const responseData = (error as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (error instanceof Error ? error.message : t('common.operationFailed'))

      toast.error({
        title: t('merchantDetail.chainReviewFailedTitle', 'Chain review update failed'),
        description: errorMessage,
        duration: 6000,
      })
    }
  }

  if (!merchantId || !isOpen) return null

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={8}>
            <SectionSkeleton lines={8} minHeight="220px" />
            <VisuallyHidden>{t('merchantDetail.loading')}</VisuallyHidden>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  if (error || !merchant) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={8}>
            <Alert status="error">
              <AlertIcon />
              {t('merchantDetail.failedToLoad')}
            </Alert>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  const availableActions = getAdminStatusActions(normalizedStatus)

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', lg: '4xl' }}>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader pb={3} pr={{ base: '88px', md: '96px' }}>
            <VStack align="stretch" spacing={1}>
              <HStack justify="space-between" align="start" spacing={3}>
                <HStack spacing={2} align="center" flexWrap="wrap" minW={0}>
                  <Text fontSize="lg" fontWeight="bold" lineHeight="short">
                    {merchant.name || t('merchantDetail.fields.unknownMerchant')}
                  </Text>
                  <Badge
                    colorScheme={getStatusColor(normalizedStatus)}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                  >
                    {getStatusText(normalizedStatus)}
                  </Badge>
                </HStack>
                <RefreshIconButton
                  label={t('common.refresh')}
                  tooltipPlacement="left"
                  onClick={handleRefresh}
                  isLoading={isRefreshing}
                  isDisabled={isRefreshing}
                  flexShrink={0}
                />
              </HStack>
              <Text fontSize="xs" color={secondaryTextColor} fontFamily="mono">
                {merchant.merchantId ?? merchant.id}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody overflowY="auto" pt={0} pb={4}>
            <Tabs
              variant="enclosed"
              colorScheme="admin"
              isLazy
              lazyBehavior="keepMounted"
              index={detailTabIndex}
              onChange={setDetailTabIndex}
            >
              <TabList
                overflowX={{ base: 'auto', md: 'visible' }}
                overflowY="hidden"
              >
                <Tab fontSize="sm" py={2}>{t('merchantDetail.tabs.basicInfo')}</Tab>
                <Tab fontSize="sm" py={2}>{t('merchantDetail.tabs.chains')}</Tab>
                <Tab fontSize="sm" py={2}>{t('merchantDetail.tabs.orderDefaults', 'Order Defaults')}</Tab>
                <Tab fontSize="sm" py={2}>{t('merchantDetail.tabs.statistics')}</Tab>
              </TabList>

              <TabPanels>
                {/* Basic info tab */}
                <TabPanel px={0} py={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('merchantDetail.merchantInfo')}</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.merchantName')}</Text>
                          <Text fontSize="sm" fontWeight="medium">{merchant.name || t('common.unknown')}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.merchantId')}</Text>
                          <Code fontSize="xs">{merchant.merchant_internal_id ?? merchant.id}</Code>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.businessId')}</Text>
                          <Code fontSize="xs">{merchant.merchantId ?? t('common.na')}</Code>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.description')}</Text>
                          <Text fontSize="sm">{merchant.description ?? t('merchantDetail.fields.noDescription')}</Text>
                        </Box>
                        {showUserId && (
                          <Box>
                            <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.userId')}</Text>
                            <Code fontSize="xs">{merchant.user_id}</Code>
                          </Box>
                        )}
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.createdAt')}</Text>
                          <Text fontSize="sm">{merchant.created_at ? formatDateTime(merchant.created_at, { locale: typeof window !== 'undefined' ? navigator.language : 'en-US' }) : t('common.na')}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={labelTextColor} mb={1}>{t('merchantDetail.fields.updatedAt')}</Text>
                          <Text fontSize="sm">{merchant.updated_at ? formatDateTime(merchant.updated_at, { locale: typeof window !== 'undefined' ? navigator.language : 'en-US' }) : t('common.na')}</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>

                    {/* Status description */}
                    <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('merchantDetail.statusExplanation')}</Text>
                      <Text fontSize="sm" color={labelTextColor}>
                        {normalizedStatus === 'mst_pending_config' && t('merchants.statusDescriptions.pendingConfig')}
                        {normalizedStatus === 'mst_partial_active' && t('merchants.statusDescriptions.partialActive')}
                        {normalizedStatus === 'mst_active' && t('merchants.statusDescriptions.active')}
                        {normalizedStatus === 'mst_suspended' && t('merchants.statusDescriptions.suspended')}
                        {normalizedStatus === 'mst_terminated' && t('merchants.statusDescriptions.terminated')}
                        {!['mst_pending_config','mst_partial_active','mst_active','mst_suspended','mst_terminated'].includes(normalizedStatus) && t('common.unknown')}
                      </Text>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Settings tab */}
                <TabPanel px={0} py={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('merchantDetail.chainAccessTitle')}</Text>
                      {isChainsLoading ? (
                        <HStack color={secondaryTextColor} spacing={3}>
                          <Spinner size="sm" />
                          <Text fontSize="sm">{t('merchantDetail.loadingChains')}</Text>
                        </HStack>
                      ) : (
                        <VStack align="stretch" spacing={2}>
                          {(merchantChains?.chains ?? []).map((chain) => (
                            <Box
                              key={chain.compatibility}
                              p={2.5}
                              border="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                            >
                              <HStack justify="space-between" align="start" spacing={3} mb={2}>
                                <HStack spacing={2} flexWrap="wrap" flex="1" minW={0}>
                                    <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
                                      {getChainLabel(chain.compatibility)}
                                    </Text>
                                    <Badge colorScheme={getApprovalBadgeColor(chain.approvalStatus)} fontSize="xs">
                                      {getApprovalLabel(chain.approvalStatus)}
                                    </Badge>
                                    {!chain.planAllowed && (
                                      <Badge colorScheme="orange" fontSize="xs">
                                        {t('merchantChainReview.planRestricted', 'Plan restricted')}
                                      </Badge>
                                    )}
                                  <Text
                                    fontSize="xs"
                                    color={secondaryTextColor}
                                    noOfLines={1}
                                    minW={{ base: '100%', lg: '260px' }}
                                    flex="1"
                                  >
                                    {chain.walletAddress || t('merchantDetail.walletAddressNotVerified')}
                                  </Text>
                                </HStack>
                                <HStack spacing={1.5} flexShrink={0}>
                                  <Button
                                    size="xs"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => setChainReviewAction({
                                      action: 'approve',
                                      compatibility: chain.compatibility,
                                    })}
                                    isDisabled={
                                      chain.approvalStatus !== 'pending_review' ||
                                      !chain.planAllowed
                                    }
                                    isLoading={
                                      approveChain.isPending
                                      && chainReviewAction?.action === 'approve'
                                      && chainReviewAction.compatibility === chain.compatibility
                                    }
                                  >
                                    {t('common.approve')}
                                  </Button>
                                  <Button
                                    size="xs"
                                    colorScheme="orange"
                                    variant="outline"
                                    onClick={() => setChainReviewAction({
                                      action: 'reject',
                                      compatibility: chain.compatibility,
                                    })}
                                    isDisabled={chain.approvalStatus !== 'pending_review'}
                                    isLoading={
                                      rejectChain.isPending
                                      && chainReviewAction?.action === 'reject'
                                      && chainReviewAction.compatibility === chain.compatibility
                                    }
                                  >
                                    {t('common.reject')}
                                  </Button>
                                </HStack>
                              </HStack>

                              <HStack spacing={1.5} flexWrap="wrap" align="stretch">
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.requestedEnabled')}:
                                    </Text>{' '}
                                    <Text
                                      as="span"
                                      color={getBooleanStatusPresentation(
                                        chain.requestedEnabled,
                                        t('common.enabled', 'Enabled'),
                                        t('common.disabled', 'Disabled')
                                      ).color}
                                      fontWeight="semibold"
                                    >
                                      {getBooleanStatusPresentation(
                                        chain.requestedEnabled,
                                        t('common.enabled', 'Enabled'),
                                        t('common.disabled', 'Disabled')
                                      ).label}
                                    </Text>
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.effectiveEnabled')}:
                                    </Text>{' '}
                                    <Text
                                      as="span"
                                      color={getBooleanStatusPresentation(
                                        chain.effectiveEnabled,
                                        t('common.enabled', 'Enabled'),
                                        t('common.disabled', 'Disabled')
                                      ).color}
                                      fontWeight="semibold"
                                    >
                                      {getBooleanStatusPresentation(
                                        chain.effectiveEnabled,
                                        t('common.enabled', 'Enabled'),
                                        t('common.disabled', 'Disabled')
                                      ).label}
                                    </Text>
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.ownershipVerified')}:
                                    </Text>{' '}
                                    <Text
                                      as="span"
                                      color={getBooleanStatusPresentation(
                                        chain.ownershipVerified,
                                        t('common.verified', 'Verified'),
                                        t('merchantDetail.notVerified')
                                      ).color}
                                      fontWeight="semibold"
                                    >
                                      {getBooleanStatusPresentation(
                                        chain.ownershipVerified,
                                        t('common.verified', 'Verified'),
                                        t('merchantDetail.notVerified')
                                      ).label}
                                    </Text>
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.walletVerifiedAt')}:
                                    </Text>{' '}
                                    {chain.walletVerifiedAt
                                      ? formatDateTime(chain.walletVerifiedAt, {
                                        locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
                                      })
                                      : t('common.na')}
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.reviewSubmittedAt')}:
                                    </Text>{' '}
                                    {chain.reviewSubmittedAt
                                      ? formatDateTime(chain.reviewSubmittedAt, {
                                        locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
                                      })
                                      : t('common.na')}
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.approvedAt')}:
                                    </Text>{' '}
                                    {chain.approvedAt
                                      ? formatDateTime(chain.approvedAt, {
                                        locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
                                      })
                                      : t('common.na')}
                                  </Text>
                                </Box>
                                <Box px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short" whiteSpace="nowrap">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.rejectedAt')}:
                                    </Text>{' '}
                                    {chain.rejectedAt
                                      ? formatDateTime(chain.rejectedAt, {
                                        locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
                                      })
                                      : t('common.na')}
                                  </Text>
                                </Box>
                              </HStack>

                              {chain.rejectionReason && (
                                <Box mt={2} px={2} py={1.5} bg={mutedCardBg} borderRadius="md">
                                  <Text fontSize="xs" lineHeight="short">
                                    <Text as="span" color={secondaryTextColor}>
                                      {t('merchantDetail.chainFields.rejectionReason')}:
                                    </Text>{' '}
                                    {chain.rejectionReason}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </TabPanel>

                <TabPanel px={0} py={4}>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="semibold">
                        {t('merchantDetail.orderDefaults.title')}
                      </Text>
                      <HStack spacing={2}>
                        <RefreshIconButton
                          label={t('merchantDetail.orderDefaults.refresh')}
                          onClick={handleRefreshOrderDefaults}
                          isLoading={isRefreshingOrderDefaults}
                          isDisabled={isRefreshingOrderDefaults || isRecomputingSnapshot}
                        />
                        <Button
                          size="sm"
                          leftIcon={<FiRefreshCw />}
                          onClick={handleRecomputeSnapshot}
                          isLoading={isRecomputingSnapshot}
                          isDisabled={isRefreshingOrderDefaults || isRecomputingSnapshot}
                        >
                          {t('merchantDetail.orderDefaults.recompute')}
                        </Button>
                      </HStack>
                    </HStack>

                    {isOrderDefaultsLoading ? (
                      <SectionSkeleton lines={6} minHeight="160px" />
                    ) : !merchantOrderDefaults ? (
                      <Alert status="warning">
                        <AlertIcon />
                        {t('merchantDetail.orderDefaults.notReady')}
                      </Alert>
                    ) : (
                      <>
                        <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <Box>
                              <Text fontSize="xs" color={labelTextColor} mb={1}>
                                {t('merchantDetail.orderDefaults.plan')}
                              </Text>
                              <Badge colorScheme="blue">
                                {merchantOrderDefaults.planDisplayName ?? merchantOrderDefaults.plan}
                              </Badge>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={labelTextColor} mb={1}>
                                {t('merchantDetail.orderDefaults.priorityLevel')}
                              </Text>
                              <Badge colorScheme="purple">
                                {merchantOrderDefaults.priorityLevel}
                              </Badge>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={labelTextColor} mb={1}>
                                {t('merchantDetail.orderDefaults.snapshotId')}
                              </Text>
                              <Code fontSize="xs">{merchantOrderDefaults.currentSnapshot.snapshotId}</Code>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={labelTextColor} mb={1}>
                                {t('merchantDetail.orderDefaults.snapshotUpdatedAt')}
                              </Text>
                              <Text fontSize="sm">
                                {formatDateTime(merchantOrderDefaults.currentSnapshot.updatedAt, {
                                  locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
                                })}
                              </Text>
                            </Box>
                          </SimpleGrid>
                        </Box>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>
                              {t('merchantDetail.orderDefaults.paymentWindow')}
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              <Text fontSize="sm">
                                {t('merchantDetail.orderDefaults.valueWithReadable', {
                                  seconds: merchantOrderDefaults.paymentWindowSeconds.value,
                                  formatted: formatOrderDefaultsDuration(merchantOrderDefaults.paymentWindowSeconds.value),
                                })}
                              </Text>
                              <Text fontSize="xs" color={secondaryTextColor}>
                                {t('merchantDetail.orderDefaults.allowedRange')}: {' '}
                                {t('merchantDetail.orderDefaults.allowedRangeValue', {
                                  min: merchantOrderDefaults.paymentWindowSeconds.min,
                                  max: merchantOrderDefaults.paymentWindowSeconds.max,
                                })}
                              </Text>
                              <Badge colorScheme={merchantOrderDefaults.paymentWindowSeconds.editable ? 'green' : 'gray'} width="fit-content">
                                {merchantOrderDefaults.paymentWindowSeconds.editable
                                  ? t('merchantDetail.orderDefaults.editable')
                                  : t('merchantDetail.orderDefaults.locked')}
                              </Badge>
                            </VStack>
                          </Box>

                          <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>
                              {t('merchantDetail.orderDefaults.afterSalesWindow')}
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              <Text fontSize="sm">
                                {t('merchantDetail.orderDefaults.valueWithReadable', {
                                  seconds: merchantOrderDefaults.afterSalesWindowSeconds.value,
                                  formatted: formatOrderDefaultsDuration(merchantOrderDefaults.afterSalesWindowSeconds.value),
                                })}
                              </Text>
                              <Text fontSize="xs" color={secondaryTextColor}>
                                {t('merchantDetail.orderDefaults.allowedRange')}: {' '}
                                {t('merchantDetail.orderDefaults.allowedRangeValue', {
                                  min: merchantOrderDefaults.afterSalesWindowSeconds.min,
                                  max: merchantOrderDefaults.afterSalesWindowSeconds.max,
                                })}
                              </Text>
                              <Badge colorScheme={merchantOrderDefaults.afterSalesWindowSeconds.editable ? 'green' : 'gray'} width="fit-content">
                                {merchantOrderDefaults.afterSalesWindowSeconds.editable
                                  ? t('merchantDetail.orderDefaults.editable')
                                  : t('merchantDetail.orderDefaults.locked')}
                              </Badge>
                            </VStack>
                          </Box>
                        </SimpleGrid>
                      </>
                    )}
                  </VStack>
                </TabPanel>

                {/* Statistics tab */}
                <TabPanel px={0} py={4}>
                  <VStack align="stretch" spacing={4}>
                    <StatGroup>
                      <Stat>
                        <StatLabel fontSize="xs">{t('merchantDetail.statistics.totalOrders')}</StatLabel>
                        <StatNumber fontSize="lg">{t('common.pending')}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs">{t('merchantDetail.statistics.totalVolume')}</StatLabel>
                        <StatNumber fontSize="lg">{t('common.pending')}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs">{t('merchantDetail.statistics.monthlyOrders')}</StatLabel>
                        <StatNumber fontSize="lg">{t('common.pending')}</StatNumber>
                      </Stat>
                    </StatGroup>

                    <Box p={3} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('merchantDetail.recentActivity')}</Text>
                      <Text fontSize="sm" color={secondaryTextColor}>
                        {t('merchantDetail.activityRecords')}
                      </Text>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>

          {availableActions.length > 0 && (
            <ModalFooter pt={2}>
              <HStack spacing={3} wrap="wrap">
                {availableActions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    colorScheme={
                      action === 'edit' ? 'yellow' :
                      action === 'approve' || action === 'activate' ? 'green' :
                      action === 'reject' ? 'orange' :
                      action === 'terminate' ? 'red' : 'gray'
                    }
                    onClick={() => handleActionClick(action)}
                    isLoading={updateStatus.isPending && confirmAction?.action === action}
                  >
                    {t(`merchantDetail.actions.${action}`)}
                  </Button>
                ))}
              </HStack>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      <StatusConfirmDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        action={confirmAction?.action || ''}
        merchantName={merchant.name}
        onConfirm={handleStatusUpdate}
        isLoading={updateStatus.isPending}
      />

      <MerchantChainReviewDialog
        isOpen={!!chainReviewAction}
        action={chainReviewAction?.action ?? null}
        merchantName={merchant.name}
        chainLabel={chainReviewAction ? getChainLabel(chainReviewAction.compatibility) : ''}
        onClose={() => setChainReviewAction(null)}
        onConfirm={handleChainReviewConfirm}
        isLoading={approveChain.isPending || rejectChain.isPending}
      />
    </>
  )
}
